/**
 * Centralised in-app route builders for the nova pages.
 *
 * These paths are owned by remote config (`config.setPages`); the builders keep
 * the literals in one place so every navigation call and the matching `:id`
 * dispatcher agree. The param is always named `id` (read with `useParams`).
 */
/** The hashes that scroll Stats to its Financial Performance and Money Flow sections. */
export const STATS_FINANCIAL_HASH = '#financial-performance';
export const STATS_MONEY_FLOW_HASH = '#money-flow';

export const ROUTE = {
  /** Home dashboard ("Business Optimization Score") — the account landing page. */
  home: (): string => '/account/home',
  /** Acquisition root (the gauge drill-in + breadcrumb target). */
  acquisition: (): string => '/account/marketing',
  /** Product root (the gauge drill-in target). */
  product: (): string => '/account/product',
  /** Operations root (the gauge drill-in target). */
  operations: (): string => '/account/operations',
  /** Suggested/Running campaign detail — a subpath of the Acquisition root. */
  campaignDetail: (id: string): string => `/account/marketing/campaign/${id}`,
  /** Channel (network) detail — web / app / crypto / short-videos. */
  channelDetail: (id: string): string => `/account/marketing/network/${id}`,
  /** Affiliates page. */
  affiliates: (): string => '/account/marketing/affiliates',
  /**
   * Affiliates create / view-edit modal as a shareable deep link. The modal is
   * query-param driven (`?affiliate=new` to create, `?affiliate=<id>` to edit) so
   * it overlays the list in place — no pathname change, no page slide.
   */
  affiliateForm: (id?: string): string => `/account/marketing/affiliates?affiliate=${encodeURIComponent(id ?? 'new')}`,
  /** Influencers page. */
  influencers: (): string => '/account/marketing/influencers',
  /** Influencers create / view-edit modal — query-param driven (mirror of {@link affiliateForm}). */
  influencerForm: (id?: string): string =>
    `/account/marketing/influencers?influencer=${encodeURIComponent(id ?? 'new')}`,
  /** Operations promotion detail (Suggested / Running). */
  operationsPromotion: (id: string): string => `/account/operations/promotion/${id}`,
  /** Product feature detail. */
  productDetail: (id: string): string => `/account/product/${id}`,
  /** Website section root: the hub, one card per Website area, with the `website-hub` flag on; with it off, a
   *  redirect to the first available tab. Groups what the owner shows on their casino site: Website Creatives,
   *  Logos, Art Style, Game Lists and Blog. */
  website: (): string => '/account/website',
  /** Website → Website Creatives: the brand's banner pool + curated carousels. */
  creatives: (): string => '/account/website/creatives',
  /** Website Creatives opened on one sub-tab: the banner pool or one carousel (read once as the initial view). */
  creativesView: (view: 'above-the-fold' | 'pool' | 'homepage' | 'casino' | 'sports'): string => `/account/website/creatives?view=${view}`,
  /** Website → Logos: the brand's logo set (download, replace). Moved here from Settings → Website Settings. */
  logos: (): string => '/account/website/logos',
  /** Logos with the upload for one slot open (read once, then removed from the URL). */
  logosReplace: (slot: string): string => `/account/website/logos?replace=${encodeURIComponent(slot)}`,
  /** Website → Art Style: the site's art style (generally available; `art-style` rollback switch). */
  artStyle: (): string => '/account/website/art-style',
  /** Art Style opened on one style. */
  artStyleFor: (id: string): string => `/account/website/art-style?style=${encodeURIComponent(id)}`,
  /** Website → Game Lists: reorder a site game list and preview it (GA; `creatives-games` rollback switch). */
  gameLists: (): string => '/account/website/games',
  /** Game Lists with Reorder lists open (read once, then removed). */
  gameListsManage: (): string => '/account/website/games?manage=1',
  /** Website → SEO: the owner's meta and footer text per route (behind `website-seo`). */
  seo: (): string => '/account/website/seo',
  /** Marketing → Marketing Assets: the custom creative studio (generally available; `creatives-assets` rollback
   *  switch). Not under `/account/marketing`, which is the Acquisition section. */
  marketingAssets: (): string => '/account/marketing-assets',
  /** Marketing → Marketing Assets → Video Ads: the grid of the brand's saved video ads. */
  creativesVideos: (): string => '/account/marketing-assets/videos',
  /** Create a new video ad (mints a draft, opens the editor). */
  creativesVideoNew: (): string => '/account/marketing-assets/videos/new',
  /** Reopen a saved video ad in the editor by entity id. */
  creativesVideo: (id: string): string => `/account/marketing-assets/videos/${encodeURIComponent(id)}`,
  /** Marketing Assets → Video Ads v2.0: the grid of the brand's single-clip video ads (the v2 pipeline). */
  creativesVideosV2: (): string => '/account/marketing-assets/videos-v2',
  /** Create a new single-clip video ad (mints a draft, opens the editor). */
  creativesVideoV2New: (): string => '/account/marketing-assets/videos-v2/new',
  /** Reopen a saved single-clip video ad in the editor by entity id. */
  creativesVideoV2: (id: string): string => `/account/marketing-assets/videos-v2/${encodeURIComponent(id)}`,
  /** Marketing Assets → Image Banners: the grid of the brand's banner kits (one ad rendered across every size). */
  creativesBannerKits: (): string => '/account/marketing-assets/banners',
  /** Create a new image banner (a banner kit: mints a draft, opens the composer). */
  creativesBannerKitNew: (): string => '/account/marketing-assets/banners/new',
  /** Reopen a saved image banner (banner kit) in the composer by entity id. */
  creativesBannerKit: (id: string): string => `/account/marketing-assets/banners/${encodeURIComponent(id)}`,
  /** Marketing Assets → Mini-games: the grid of the brand's playable HTML5 mini-game ads. */
  creativesPlayables: (): string => '/account/marketing-assets/mini-games',
  /** Create a new mini-game (mints a draft, opens the composer). */
  creativesPlayableNew: (): string => '/account/marketing-assets/mini-games/new',
  /** Reopen a saved mini-game in the composer by entity id. */
  creativesPlayable: (id: string): string => `/account/marketing-assets/mini-games/${encodeURIComponent(id)}`,
  /** Marketing Assets → HTML5 Banners: the grid of the brand's animated live-text HTML5 banners. */
  creativesHtml5Banners: (): string => '/account/marketing-assets/html5-banners',
  /** Create a new HTML5 banner. */
  creativesHtml5BannerNew: (): string => '/account/marketing-assets/html5-banners/new',
  /** Reopen a saved HTML5 banner by entity id. */
  creativesHtml5Banner: (id: string): string => `/account/marketing-assets/html5-banners/${encodeURIComponent(id)}`,
  /** Marketing Assets → Landing Pages: the brand's live landing pages on its own landing domain. */
  creativesLandingPages: (): string => '/account/marketing-assets/landing-pages',
  /** Compose a new landing page (template, offer, copy, live preview). */
  creativesLandingPageNew: (): string => '/account/marketing-assets/landing-pages/new',
  /** Reopen a saved landing page by entity id. */
  creativesLandingPage: (id: string): string => `/account/marketing-assets/landing-pages/${encodeURIComponent(id)}`,
  /** Players — the casino's full player roster (table). */
  players: (): string => '/account/players',
  /** Wallet (balance / settlement / activity) — mounted as a fixed route, not via ModuleElement. */
  wallet: (): string => '/account/wallet',
  /** Ad Budget & Limits (wallet) — mounted as a fixed route, not via ModuleElement. */
  walletBudget: (): string => '/account/wallet/budget',
  /** Account settings index (borrows the account-home layout chrome). */
  settings: (): string => '/account/settings',
  /**
   * A single Settings section as its own route — `profile` / `website-settings`
   * (domain details and website currency; the logos moved to {@link ROUTE.logos}) /
   * `billing` / `account` / `communications`. Drilled into from the Settings index.
   */
  settingsSection: (section: string): string => `/account/settings/${section}`,
  /**
   * One mailbox's Setup Mailbox page in the Mailbox section. Its own
   * route, not a view flag, so refresh and deep links land on the mailbox. Nothing constructs this
   * path by hand: `router.tsx` mounts it off {@link MAILBOX_DETAIL_PATH}. Keyed on the address, as
   * the API is; encoded because an address carries `@` (React Router decodes the param back).
   */
  mailboxDetail: (email: string): string => `/account/settings/mailbox/${encodeURIComponent(email)}`,
  /** Customizer ("Take your website live") — the POST-payment onboarding wizard that
   *  finishes setting up a new casino. Named `/account/onboarding` to avoid confusion
   *  with the pre-payment `/setup` wizard. */
  customizer: (): string => '/account/onboarding',
  /** Pulse business overview (borrows the account-home layout chrome). */
  pulse: (): string => '/account/pulse',
  /** Stats — the live brand / operational dashboard (reuses its own config chrome).
   *  (The Contact support form is now a Settings subpage — `settingsSection('contact')`.) */
  stats: (): string => '/account/stats',
  /** Stats → Website: visitor stats of the brand's website and landing pages (a tab of Stats). */
  statsWebsite: (): string => '/account/stats/website',
  /** Stats, scrolled to its Financial Performance section (Home's financial figures link here). */
  statsFinancial: (): string => `/account/stats${STATS_FINANCIAL_HASH}`,
  /** Stats, scrolled to its Money Flow section (Home's Money Flow card links here). */
  statsMoneyFlow: (): string => `/account/stats${STATS_MONEY_FLOW_HASH}`,
  /** Website → Blog: authoring home (article list). */
  blog: (): string => '/account/website/blog',
  blogNew: (): string => '/account/website/blog/new',
  blogEdit: (id: string): string => `/account/website/blog/${id}/edit`,
  blogPreview: (id: string): string => `/account/website/blog/${id}/preview`,
  /**
   * Password-reset / set-password request page (SPB-480), the target of the login page's
   * "Forgot password?" link. The page (`pages/reset-password`) owns BOTH the request form
   * (`POST /auth/forgot-password` with `{ email }`) and the set-new-password redemption view
   * (`POST /auth/reset-password` with `{ code, new_password }`), reached via the emailed
   * `?code=` link. Both are the shipped micros-wl_owner_hub contract. The redemption view is
   * also mounted at `/reset_password` (underscore) because the BE email links there; this
   * builder returns the hyphen path used by the in-app "Forgot password?" link.
   */
  resetPassword: (): string => '/reset-password',
  /**
   * Authenticated change-password page (SPB-480). Only a signed-in owner reaches it (the page
   * gates on `useAuthGate` and sends a signed-out visitor to `/login`). The owner requests an
   * emailed confirmation code (`GET /auth/change-password-request`) then sets a new password with
   * it (`POST /auth/change-password-confirm` with `{ code, new_password }`). SINGLE mount: the BE
   * emails the hyphen path `{ownerHubFrontUrl}/change-password?code=`, so there is no underscore
   * alias (unlike {@link resetPassword}).
   */
  changePassword: (): string => '/change-password',
} as const;

/** The route pattern behind `ROUTE.mailboxDetail`, for `router.tsx` and the CT story host. */
export const MAILBOX_DETAIL_PATH = '/account/settings/mailbox/:email';
